import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { applicationsApi } from "api/applications";
import { toast } from "react-toastify";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show,
  TextArea
} from "shared/components";
import { Modal } from "shared/components/Modal";
import { useFormTyped } from "shared/hooks";
import { getErrorMessage } from "utils/error";
import { object, string } from "yup";

const notesValidation = object().shape({
  note: string()
    .required()
    .min(10, "At least 10 characters")
    .max(2048, "Text is too long")
});

export const DocumentsRequestModal = ({
  onClose,
  applicationId,
  applicantId,
  queryKey
}: {
  onClose: () => void;
  applicationId: string;
  applicantId: string;
  queryKey?: unknown[];
}) => {
  const queryClient = useQueryClient();
  const form = useFormTyped(
    {
      note: ""
    },
    notesValidation
  );

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const documentRequestMutation = useMutation({
    mutationKey: ["APPLICATION", "CREATE_DOCUMENT_REQUEST"],
    mutationFn: (note: string) =>
      applicationsApi.createDocumentRequest({
        applicantId,
        applicationId,
        note
      }),
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey
      });
      toast.success("Request has been sent to the applicant");
      handleClose();
    }
  });

  return (
    <Modal
      opened
      onClose={handleClose}
      panelClassName="max-w-[478px] w-full flex flex-col"
    >
      <header className="flex justify-between items-center h-[56px] gap-2 border-b border-alpha-100 px-6">
        <h1 className="text-base font-bold text-content-1000">
          Request additional documents
        </h1>

        <IconButton
          variant={IconButtonVariant.TEXT_BASE}
          size={IconButtonSize.LG}
          onClick={handleClose}
          HugeIcon={Cancel01Icon}
        />
      </header>
      <form
        id="note-form"
        className="p-6 max-h-[500px] grow overflow-y-auto bg-background-900"
        onSubmit={form.onSubmit({
          onSuccess: ({ note }) => {
            documentRequestMutation.mutate(note);
          }
        })}
      >
        <legend className="text-sm font-medium text-content-400 mb-4">
          Boom will send a request to submit additional documents in the
          application. Please add a note specifying the required documents
        </legend>

        <TextArea
          rows={5}
          id="note"
          legacyErrorMargins={false}
          placeholder="Add a note"
          {...form.register("note")}
        />

        <Show if={form.errors.note}>
          <p className="text-danger-500 text-xs mt-2">{form.errors.note}</p>
        </Show>
      </form>
      <footer className="h-[72px] flex justify-end items-center border-t border-alpha-100 gap-2 px-6">
        <Button onClick={handleClose} size={ButtonSize.SM}>
          Cancel
        </Button>
        <Button
          size={ButtonSize.SM}
          loading={documentRequestMutation.isPending}
          variant={ButtonVariant.PRIMARY}
          type="submit"
          form="note-form"
          disabled={!form.values.note.length}
        >
          Send request
        </Button>
      </footer>
    </Modal>
  );
};
